/* iter-51e — Canva Cover Designer dialog.
 *
 * Per-author cover-art workflow:
 *   1. Connect Canva  → OAuth pop-up to canva.com (PKCE handled server-side)
 *   2. Design new cover → backend creates a blank 1600×2560 design in the
 *      author's own Canva account, returns an edit URL. We open that URL in
 *      a new tab so the author can design.
 *   3. Save to project → backend exports PNG via Canva API, stores it in
 *      GridFS under the project_cover_art bucket ("their project cover art"
 *      folder per user spec).
 *
 * Covers list shows all the author's saved cover PNGs for the current
 * The Loom project. Each can be deleted or downloaded.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Palette, Link2, ExternalLink, Trash2, Image as ImageIcon, RefreshCw, AlertCircle, Plug, ShoppingCart } from 'lucide-react';
import { usePW } from '@/lib/store';
import { CanvaPulseCard } from '@/components/community/CanvaPulseCard';
import { canvaApi } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function CanvaCoverDialog({ open, onOpenChange }) {
  const { project } = usePW();
  const [status, setStatus] = useState(null);  // {configured, connected}
  const [covers, setCovers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingDesign, setPendingDesign] = useState(null);   // {design_id, editor_url}
  const [coverCredits, setCoverCredits] = useState(null);    // null = loading
  const [coverPrice, setCoverPrice] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/canva/status`);
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, []);

  const loadCovers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/canva/cover/list`, {
        params: project?.id ? { project_id: project.id } : {},
      });
      setCovers(r.data?.covers || []);
    } catch {
      setCovers([]);
    }
  }, [project?.id]);

  useEffect(() => {
    if (!open) return;
    loadStatus();
    loadCovers();
    // Also load cover credit info
    canvaApi.coverPrice().then((r) => {
      setCoverCredits(r.cover_credits ?? 0);
      setCoverPrice(r.price_usd ?? null);
    }).catch(() => {
      setCoverCredits(0);
      setCoverPrice(null);
    });
  }, [open, loadStatus, loadCovers]);

  // After the OAuth tab closes it postMessages back; refresh status.
  useEffect(() => {
    if (!open) return;
    const expectedOrigin = window.location.origin;
    const onMsg = (ev) => {
      if (ev.origin !== expectedOrigin) return;            // iter-51h
      if (ev.data?.type === 'canva_oauth') {
        if (ev.data.success) toast.success('Canva connected.');
        else                   toast.error('Canva connection failed.');
        loadStatus();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [open, loadStatus]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/canva/connect`);
      const url = r.data?.url;
      if (!url) throw new Error('no url');
      window.open(url, 'canva_oauth', 'width=720,height=820,noopener');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Canva auth.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Canva for your account?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/canva/disconnect`);
      toast.success('Canva disconnected.');
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed.');
    } finally {
      setBusy(false);
    }
  };

  const buyCredit = async () => {
    setPurchasing(true);
    try {
      const r = await canvaApi.coverPurchaseCheckout();
      if (r?.url) window.location.href = r.url;
      else toast.error('Checkout URL missing from response.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
    } finally {
      setPurchasing(false);
    }
  };

  const createCover = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/canva/cover/create`, {
        project_id: project?.id || null,
        title:      project?.title ? `${project.title} — cover` : 'Book cover',
      });
      // Deduct one credit locally (server already decremented)
      setCoverCredits((n) => Math.max(0, (n ?? 1) - 1));
      setPendingDesign(r.data);
      if (r.data?.editor_url) window.open(r.data.editor_url, '_blank', 'noopener');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.code === 'canva_cover_no_credits') {
        // Credits exhausted — switch to purchase gate
        setCoverCredits(0);
        toast.info('You need a cover credit to design. Purchase one below.');
      } else if (e?.response?.status === 402) {
        toast.error('Connect your Canva account under Preferences → Connections first.');
      } else {
        toast.error(detail || 'Could not create a Canva design.');
      }
    } finally {
      setBusy(false);
    }
  };

  const saveCover = async () => {
    if (!pendingDesign?.design_id) return;
    setBusy(true);
    toast.message('Exporting from Canva… (may take ~10–60s)');
    try {
      const r = await axios.post(`${API}/canva/cover/save`, {
        design_id:  pendingDesign.design_id,
        project_id: project?.id || null,
        label:      project?.title || 'Cover',
      });
      toast.success('Cover saved to project_cover_art.');
      const af = r.data?.isbn_autofill;
      if (af) {
        const bits = [];
        if (af.trim_size)  bits.push(`trim ${af.trim_size}`);
        if (af.page_count) bits.push(`~${af.page_count} pages`);
        if (bits.length) toast.success(`ISBN record auto-filled: ${bits.join(' · ')}.`);
      }
      setPendingDesign(null);
      await loadCovers();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error('Connect your Canva account under Preferences → Connections first.');
      } else {
        toast.error(e?.response?.data?.detail || 'Export failed (check for Pro elements in Canva).');
      }
    } finally {
      setBusy(false);
    }
  };

  const removeCover = async (cid) => {
    if (!window.confirm('Delete this cover?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/canva/cover/${cid}`);
      toast.success('Removed.');
      await loadCovers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="canva-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" /> Design a cover with Canva
          </DialogTitle>
          <DialogDescription>
            Open Canva in a new tab to design your book cover. When you're
            finished, come back and click <strong>Save cover to project</strong> —
            the PNG is stored under your project's cover-art folder.
          </DialogDescription>
        </DialogHeader>

        {/* Connection panel */}
        {!status ? (
          <Loader text="Checking Canva connection…" />
        ) : !status.configured ? (
          <NotConfiguredCard redirectUri={status.redirect_uri} />
        ) : !status.connected ? (
          <>
            <ConnectCard onConnect={connect} busy={busy} />
            {/* iter55z-i — Social-proof pulse only shown to unlinked users. */}
            <CanvaPulseCard variant="full" onConnectClick={connect} testIdPrefix="canva-dialog-pulse" />
          </>
        ) : (
          <ConnectedRow onDisconnect={disconnect} busy={busy} />
        )}

        {/* iter55z-h — Wallet panel removed (BYO Canva: users authenticate
            with their own Canva account and pay via their own quota). */}

        {/* Action row — only when connected */}
        {status?.connected && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-3">
            {/* Cover credit gate */}
            {coverCredits === null ? (
              <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground" data-testid="canva-credits-loading">
                <Loader2 className="h-3 w-3 animate-spin" /> Checking cover credits…
              </div>
            ) : coverCredits === 0 ? (
              <div className="flex w-full flex-col gap-2" data-testid="canva-no-credits-gate">
                <p className="text-[11.5px] text-muted-foreground">
                  Each new cover design uses one cover credit.
                  {coverPrice && <span className="ml-1 font-semibold text-foreground">${Number(coverPrice).toFixed(2)} per cover.</span>}
                </p>
                <button
                  type="button"
                  onClick={buyCredit}
                  disabled={purchasing}
                  data-testid="canva-buy-credit-btn"
                  className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                >
                  {purchasing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShoppingCart className="h-3 w-3" />}
                  {purchasing ? 'Opening checkout…' : `Buy a cover credit${coverPrice ? ` — $${Number(coverPrice).toFixed(2)}` : ''}`}
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={createCover}
                  disabled={busy}
                  data-testid="canva-create"
                  className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Palette className="h-3 w-3" />}
                  Design new cover
                </button>
                <span className="text-[10.5px] text-muted-foreground" data-testid="canva-credit-count">
                  {coverCredits} credit{coverCredits !== 1 ? 's' : ''} remaining
                </span>
              </>
            )}

            {pendingDesign && (
              <>
                <a
                  href={pendingDesign.editor_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="canva-reopen"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2.5 py-1.5 text-[11.5px] hover:bg-secondary"
                >
                  <ExternalLink className="h-3 w-3" /> Re-open in Canva
                </a>
                <button
                  type="button"
                  onClick={saveCover}
                  disabled={busy}
                  data-testid="canva-save"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary/10 px-3 py-1.5 text-[11.5px] font-semibold text-primary hover:bg-primary/15 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
                  Save cover to project
                </button>
              </>
            )}

            <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
              <AlertCircle className="h-3 w-3" />
              Free Canva accounts: avoid Pro elements or the export will fail.
            </span>
          </div>
        )}

        {/* Saved covers */}
        <section className="mt-2">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              {project?.title ? `Cover art — ${project.title}` : 'Project cover art'}
            </h3>
            <button
              type="button"
              onClick={loadCovers}
              disabled={busy}
              data-testid="canva-refresh"
              className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[10.5px] hover:bg-secondary disabled:opacity-60"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
          {covers === null ? (
            <Loader text="Loading covers…" />
          ) : covers.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-card/40 p-6 text-center text-[12px] text-muted-foreground">
              No covers saved yet. Click <strong>Design new cover</strong> above to get started.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="canva-covers-grid">
              {covers.map((c) => <CoverTile key={c.id} cover={c} onDelete={() => removeCover(c.id)} />)}
            </div>
          )}
        </section>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────── sub-widgets


function NotConfiguredCard({ redirectUri }) {
  // iter-53j — User-facing copy that doesn't leak admin jargon. The
  // redirect-URI hint moved to the admin Keys panel where it actually
  // belongs (writers don't register OAuth apps).
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-[12.5px] text-amber-950" data-testid="canva-not-configured">
      <div className="font-semibold">Canva covers are on the way.</div>
      <p className="mt-1.5">
        We're waiting on Canva's enterprise-tier approval before we can
        switch this on. In the meantime:
      </p>
      <ul className="mt-1.5 ml-4 list-disc space-y-1 text-[12px]">
        <li>
          <strong>Use the AI cover generator</strong> on this book's page
          (Gemini Nano Banana, ~5 seconds, no setup needed).
        </li>
        <li>
          <strong>Or upload your own</strong> via the book's
          <em> Cover art </em> panel — we auto-build the storefront
          thumbnail and tie it to the ISBN.
        </li>
      </ul>
      <p className="mt-2 text-[11px] text-amber-900/80">
        Your existing Canva designs and pre-paid cover credits will
        transfer automatically when the integration goes live.
      </p>
    </div>
  );
}

function ConnectCard({ onConnect, busy }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-4">
      <div>
        <div className="text-[12px] font-semibold">Connect your Canva account</div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Your covers live in your own Canva account. We only export the
          finished PNG when you tell us to.
        </p>
      </div>
      <button
        type="button"
        onClick={onConnect}
        disabled={busy}
        data-testid="canva-connect"
        className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
        Connect Canva
      </button>
    </div>
  );
}

function ConnectedRow({ onDisconnect, busy }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-300/60 bg-emerald-50 px-3 py-2 text-[11.5px] text-emerald-900" data-testid="canva-connected">
      <div className="inline-flex items-center gap-1.5">
        <Link2 className="h-3.5 w-3.5" /> Canva is connected to your account.
      </div>
      <button
        type="button"
        onClick={onDisconnect}
        disabled={busy}
        data-testid="canva-disconnect"
        className="inline-flex items-center gap-1 rounded-sm border border-emerald-400 bg-white px-2 py-0.5 text-[10.5px] hover:bg-emerald-100 disabled:opacity-60"
      >
        Disconnect
      </button>
    </div>
  );
}

function CoverTile({ cover, onDelete }) {
  return (
    <div className="group flex flex-col gap-1 overflow-hidden rounded-sm border border-border bg-card" data-testid={`canva-cover-${cover.id}`}>
      <div className="relative aspect-[5/8] w-full bg-secondary">
        <img
          src={(process.env.REACT_APP_BACKEND_URL || '') + cover.url}
          alt={cover.label || cover.filename}
          loading="lazy"
          className="h-full w-full object-cover"
        />
        <button
          type="button"
          onClick={onDelete}
          data-testid={`canva-cover-delete-${cover.id}`}
          className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-sm border border-border bg-white/85 opacity-0 transition group-hover:opacity-100 hover:bg-rose-50"
          title="Delete"
        >
          <Trash2 className="h-3 w-3 text-rose-700" />
        </button>
      </div>
      <div className="px-2 py-1.5">
        <div className="line-clamp-1 text-[11px] font-semibold">{cover.label || 'Cover'}</div>
        <div className="text-[10px] text-muted-foreground">{new Date(cover.created_at).toLocaleString()}</div>
      </div>
    </div>
  );
}

function Loader({ text }) {
  return (
    <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> {text}
    </div>
  );
}
